import { keepPreviousData, QueryCache, QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch, sdk } from "./sdk"
import type { ActivityActor, ActivityEntry, Dashboard, InviteLookup, Me, Permission, Role, StaffDetail, StaffInvite, StaffList, StaffMember } from "./types"

/** Pages anyone can open (no session): never bounce them to /login. */
export const PUBLIC_PATHS = ["/login", "/invite", "/forgot-password", "/reset-password"]
const isPublicPath = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (e: any) => {
      // Session expired, signed out by an owner, or account deactivated → back to login.
      // two_factor_required: the session is fine, it just needs its code (RequireSession shows the prompt).
      // two_factor_setup_required: the store requires two-step verification (RequireSession shows the setup).
      if ((e?.status === 401 && e?.code === "two_factor_required") || (e?.status === 403 && e?.code === "two_factor_setup_required")) {
        void queryClient.invalidateQueries({ queryKey: ["me"] })
        return
      }
      if (e?.status === 401 && !isPublicPath()) {
        const reason = e?.code === "account_deactivated" || e?.code === "session_revoked" ? `&reason=${e.code}` : ""
        window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}${reason}`)
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, e: any) => e?.status !== 401 && e?.status !== 403 && e?.status !== 404 && count < 2,
    },
  },
})

export const qk = {
  me: ["me"] as const,
  dashboard: (range: string) => ["dashboard", range] as const,
  staff: ["staff"] as const,
  staffMember: (id: string) => ["staff", id] as const,
  activity: (p: object) => ["activity", p] as const,
  products: (p: object) => ["products", p] as const,
  product: (id: string) => ["product", id] as const,
  orders: (p: object) => ["orders", p] as const,
  order: (id: string) => ["order", id] as const,
  customers: (p: object) => ["customers", p] as const,
  customer: (id: string) => ["customer", id] as const,
}

// ── Session ────────────────────────────────────────────────────────────────

/** null = not signed in. */
export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await apiFetch<Me>("/admin/terify/me")
      } catch (e: any) {
        if (e?.status === 401) {
          // Deactivated or signed out by an admin: show why on the sign-in page.
          if ((e?.code === "account_deactivated" || e?.code === "session_revoked") && !isPublicPath()) {
            const next = encodeURIComponent(window.location.pathname + window.location.search)
            window.location.assign(`/login?next=${next}&reason=${e.code}`)
          }
          return null
        }
        throw e
      }
    },
    staleTime: 5 * 60_000,
  })
}

const SIGN_IN_ERRORS: Record<string, string> = {
  account_deactivated: "This staff account has been deactivated. Ask a store owner to reactivate it.",
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const res = await sdk.auth.login("user", "emailpass", input)
      if (typeof res !== "string") throw new Error("Additional sign-in step required (not supported yet)")
      // Surface "deactivated" here instead of silently bouncing back to the form.
      try {
        await apiFetch<Me>("/admin/terify/me")
      } catch (e: any) {
        if (e?.code && SIGN_IN_ERRORS[e.code]) {
          await sdk.auth.logout().catch(() => undefined)
          throw new Error(SIGN_IN_ERRORS[e.code])
        }
        throw e
      }
      return res
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  })
}

export async function logout() {
  try {
    await sdk.auth.logout()
  } finally {
    queryClient.clear()
    window.location.assign("/login")
  }
}

// ── Your account ───────────────────────────────────────────────────────────

export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { first_name?: string; last_name?: string }) => sdk.client.fetch<Me>("/admin/terify/me", { method: "POST", body }),
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      sdk.client.fetch<{ success: true }>("/admin/terify/me/password", { method: "POST", body }),
  })
}

// ── Public: invitations and password reset (no session) ────────────────────

export function useInviteLookup(token: string) {
  return useQuery({
    queryKey: ["invite", token],
    enabled: !!token,
    queryFn: () => sdk.client.fetch<InviteLookup>("/admin/terify/invite", { query: { token } }),
    retry: false,
  })
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: (body: { token: string; first_name: string; last_name: string; password: string }) =>
      sdk.client.fetch<{ email: string }>("/admin/terify/invite/accept", { method: "POST", body }),
  })
}

export function useRequestPasswordReset() {
  return useMutation({ mutationFn: (email: string) => sdk.auth.resetPassword("user", "emailpass", { identifier: email }) })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, email, password }: { token: string; email: string; password: string }) =>
      sdk.auth.updateProvider("user", "emailpass", { email, password }, token),
  })
}

// ── Terify endpoints ───────────────────────────────────────────────────────

export function useDashboard(range: Dashboard["range"]) {
  return useQuery({
    queryKey: qk.dashboard(range),
    queryFn: () => sdk.client.fetch<Dashboard>("/admin/terify/dashboard", { query: { range } }),
    placeholderData: keepPreviousData,
  })
}

// ── Users and permissions ──────────────────────────────────────────────────

export function useStaff() {
  return useQuery({ queryKey: qk.staff, queryFn: () => sdk.client.fetch<StaffList>("/admin/terify/staff") })
}

export function useStaffMember(id: string) {
  return useQuery({ queryKey: qk.staffMember(id), queryFn: () => sdk.client.fetch<StaffDetail>(`/admin/terify/staff/${id}`) })
}

export type InviteInput = { email: string; first_name?: string; last_name?: string; role: Exclude<Role, "owner">; permissions: Permission[] }

export function useInviteStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: InviteInput) => sdk.client.fetch<{ invite: StaffInvite }>("/admin/terify/staff/invites", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  })
}

export function useResendInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sdk.client.fetch<{ invite: StaffInvite }>(`/admin/terify/staff/invites/${id}/resend`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  })
}

export function useRevokeInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sdk.client.fetch(`/admin/terify/staff/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  })
}

export function useUpdateStaff(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { role?: Exclude<Role, "owner">; permissions?: Permission[]; active?: boolean }) =>
      sdk.client.fetch<{ staff: StaffMember }>(`/admin/terify/staff/${id}`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.staff })
    },
  })
}

export function useSignOutStaff(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/staff/${id}/sign-out`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staffMember(id) }),
  })
}

export function useRemoveStaff(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/staff/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  })
}

export function useTransferOwnership() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { staff_id: string; password: string }) =>
      sdk.client.fetch<{ owner: StaffMember }>("/admin/terify/staff/transfer-ownership", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.staff })
      qc.invalidateQueries({ queryKey: qk.me })
    },
  })
}

/** Make an active staff member a co-owner (owners only, fewer than 2 owners). */
export function useMakeOwner(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { password: string }) => sdk.client.fetch<{ staff: StaffMember }>(`/admin/terify/staff/${id}/owner`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.staff })
      qc.invalidateQueries({ queryKey: qk.me })
    },
  })
}

/** The signed-in owner steps down to full permissions (only while another owner remains). */
export function useStepDown() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { password: string }) => sdk.client.fetch<{ role: Role; owners: number }>("/admin/terify/me/step-down", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.staff })
      qc.invalidateQueries({ queryKey: qk.me })
    },
  })
}

/** The holder of log access hands it to someone else (it moves, it isn't copied). */
export function useGiveLogAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { staff_id: string; password: string }) => sdk.client.fetch<{ log_access: { staff_id: string; email: string } }>("/admin/terify/me/log-access", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.staff })
      qc.invalidateQueries({ queryKey: qk.me })
    },
  })
}

export type ActivityParams = {
  limit: number
  offset: number
  area?: string
  actor_id?: string
  resource_id?: string
  q?: string
  result?: "ok" | "failed" | "blocked"
  from?: string
  to?: string
  hide_sign_ins?: "1"
}
export function useActivity(params: ActivityParams) {
  return useQuery({
    queryKey: qk.activity(params),
    queryFn: () =>
      sdk.client.fetch<{ activity: ActivityEntry[]; count: number; actors?: ActivityActor[] }>("/admin/terify/activity", {
        query: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "")),
      }),
    placeholderData: keepPreviousData,
  })
}

// ── Medusa resources ───────────────────────────────────────────────────────

type ListParams = { q?: string; limit: number; offset: number }
const clean = (p: ListParams) => ({ ...p, q: p.q?.trim() || undefined })

export function useProducts(p: ListParams & { status?: string; type_id?: string; collection_id?: string; tag_id?: string; order?: string }) {
  return useQuery({
    queryKey: qk.products(p),
    queryFn: () =>
      sdk.admin.product.list({
        ...clean(p),
        status: p.status ? [p.status as any] : undefined,
        type_id: p.type_id ? [p.type_id] : undefined,
        collection_id: p.collection_id ? [p.collection_id] : undefined,
        tag_id: p.tag_id ? [p.tag_id] : undefined,
        order: p.order || "title",
        fields: "id,title,handle,status,thumbnail,created_at,updated_at,+metadata,*tags,*variants,+variants.manage_inventory,*variants.inventory_items,*variants.inventory_items.inventory,*variants.inventory_items.inventory.location_levels,*type,*collection",
      }),
    placeholderData: keepPreviousData,
  })
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: qk.product(id),
    queryFn: () =>
      sdk.admin.product.retrieve(id, {
        fields: "*variants,*variants.prices,*images,*options,*tags,*type,*collection,*sales_channels,+metadata,+external_id",
      }),
  })
}

export function useOrders(p: ListParams) {
  return useQuery({
    queryKey: qk.orders(p),
    queryFn: () =>
      sdk.admin.order.list({
        ...clean(p),
        order: "-created_at",
        fields: "id,display_id,email,total,currency_code,status,payment_status,fulfillment_status,created_at,*customer",
      }),
    placeholderData: keepPreviousData,
  })
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: qk.order(id),
    queryFn: () =>
      sdk.admin.order.retrieve(id, {
        fields:
          "*items,*shipping_address,*billing_address,*shipping_methods,*customer,total,subtotal,shipping_total,tax_total,discount_total,payment_status,fulfillment_status,+metadata",
      }),
  })
}

export function useCustomers(p: ListParams) {
  return useQuery({
    queryKey: qk.customers(p),
    queryFn: () =>
      sdk.admin.customer.list({
        ...clean(p),
        order: "-created_at",
        fields: "id,email,first_name,last_name,phone,has_account,created_at,+metadata",
      }),
    placeholderData: keepPreviousData,
  })
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: qk.customer(id),
    queryFn: () => sdk.admin.customer.retrieve(id, { fields: "*addresses,+metadata" }),
  })
}
